import { ownAccountSchema } from "@rosc/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";
import {
  languageDisplayName,
  noteLanguageForInput,
  readSameAsConversation,
  writeSameAsConversation,
} from "@/lib/note-language";
import { referenceClient } from "@/lib/orpc/client";
import { getVisitScope } from "@/lib/visit-queries";
import { useAuthStore } from "@/store/auth.store";
import { useSettingsOptions } from "@/store/settingsOptions.store";

export interface NoteLanguageOption extends TranscriptionLanguage {
  /** For lists: "Français". */
  label: string;
  /** Inside a sentence: "français". */
  inline: string;
}

export interface NoteLanguageState {
  options: NoteLanguageOption[];
  current: NoteLanguageOption | undefined;
  sameAsConversation: boolean;
  loading: boolean;
  saving: boolean;
  failed: boolean;
  chooseLanguage: (id: number) => Promise<void>;
  chooseSameAsConversation: (inputCode: string | undefined) => Promise<void>;
  syncWithInput: (inputCode: string | undefined) => Promise<void>;
}

// Saves the account's note language and reloads the account, so Settings,
// the New visit sheet and the workspace show the same value.
const saveNoteLanguage = async (id: number): Promise<boolean> => {
  const scope = getVisitScope();
  if (!scope) return false;
  const account = ownAccountSchema.parse(
    await referenceClient.updateMyPreferences(
      { body: { transcriptionLanguageId: id } },
      { context: { expectedUserUid: scope.uid } },
    ),
  );
  if (getVisitScope() !== scope || account.public_id !== scope.accountPublicId)
    return false;
  await useAuthStore.getState().getUser();
  return true;
};

export const useNoteLanguage = (): NoteLanguageState => {
  const { t, i18n } = useTranslation();
  const userData = useAuthStore((state) => state.userData);
  const accountId = userData?.public_id;
  const currentId = userData?.userPreference?.transcriptionLanguageId;
  const {
    transcriptionLanguages,
    loading,
    fetch: fetchOptions,
  } = useSettingsOptions();
  const [sameAsConversation, setSameAsConversation] = useState(() =>
    readSameAsConversation(accountId),
  );
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSameAsConversation(readSameAsConversation(accountId));
  }, [accountId]);

  useEffect(() => {
    const state = useSettingsOptions.getState();
    if (!state.transcriptionLanguages.length && !state.loading)
      void fetchOptions();
  }, [fetchOptions]);

  const mandarin = t("noteLanguage.mandarin");
  const options = useMemo(
    () =>
      transcriptionLanguages.map((language) => ({
        ...language,
        label: languageDisplayName(language.name, i18n?.language, mandarin),
        inline: languageDisplayName(language.name, i18n?.language, mandarin, {
          inline: true,
        }),
      })),
    [transcriptionLanguages, i18n?.language, mandarin],
  );
  const current = options.find((option) => option.id === currentId);

  const save = useCallback(async (id: number): Promise<void> => {
    // The saved value now, not this render's: a pick made right after
    // another save (the welcome, then New visit) compares with the latest.
    if (
      id ===
      useAuthStore.getState().userData?.userPreference?.transcriptionLanguageId
    )
      return;
    setSaving(true);
    setFailed(false);
    try {
      if (!(await saveNoteLanguage(id))) setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }, []);

  const chooseLanguage = useCallback(
    async (id: number): Promise<void> => {
      writeSameAsConversation(accountId, false);
      setSameAsConversation(false);
      await save(id);
    },
    [accountId, save],
  );

  const syncWithInput = useCallback(
    async (inputCode: string | undefined): Promise<void> => {
      // The language list may still be loading (the welcome is the first
      // screen of a new account): wait for it instead of skipping the save,
      // or the note would stay in the old language.
      let languages = transcriptionLanguages;
      if (!languages.length) {
        if (!useSettingsOptions.getState().transcriptionLanguages.length)
          await useSettingsOptions.getState().fetch();
        languages = useSettingsOptions.getState().transcriptionLanguages;
      }
      if (!languages.length) {
        if (inputCode && inputCode !== "auto") setFailed(true);
        return;
      }
      const match = noteLanguageForInput(inputCode, languages);
      if (match) await save(match.id);
    },
    [transcriptionLanguages, save],
  );

  const chooseSameAsConversation = useCallback(
    async (inputCode: string | undefined): Promise<void> => {
      writeSameAsConversation(accountId, true);
      setSameAsConversation(true);
      await syncWithInput(inputCode);
    },
    [accountId, syncWithInput],
  );

  return {
    options,
    current,
    sameAsConversation,
    loading: loading && !transcriptionLanguages.length,
    saving,
    failed,
    chooseLanguage,
    chooseSameAsConversation,
    syncWithInput,
  };
};
